import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type {
  GriefingRisk,
  OpFrequency,
  PatchWarPlanInput,
  StockManagement,
  WarPlanDTO,
  WarStyle,
} from '@warplan/shared';
import { useWarPlanSource as useWarPlan, useUpdateWarPlanSource as useUpdateWarPlan } from '../lib/map-data-source.js';
import { useWarPlanPanel } from '../state/war-plan-panel-store.js';

export interface WarPlanPanelProps {
  /** When false, every control is read-only. */
  canEdit: boolean;
}

type Severity = 'red' | 'orange' | 'green';

interface Option<T extends string> {
  value: T;
  fr: string;
  en: string;
  severity: Severity;
}

const WAR_STYLE_OPTIONS: ReadonlyArray<Option<WarStyle>> = [
  { value: 'offensive', fr: 'Offensive', en: 'Offensive', severity: 'red' },
  { value: 'balanced', fr: 'Équilibré', en: 'Balanced', severity: 'orange' },
  { value: 'defensive', fr: 'Défensive', en: 'Defensive', severity: 'green' },
];

const OP_FREQUENCY_OPTIONS: ReadonlyArray<Option<OpFrequency>> = [
  { value: 'high', fr: 'Fréquence Haute', en: 'High Frequency', severity: 'red' },
  { value: 'medium', fr: 'Fréquence Moyenne', en: 'Medium Frequency', severity: 'orange' },
  { value: 'low', fr: 'Fréquence Basse', en: 'Low Frequency', severity: 'green' },
];

const STOCK_OPTIONS: ReadonlyArray<Option<StockManagement>> = [
  {
    value: 'frontline_supply',
    fr: 'Alimentation du front',
    en: 'Frontline supplying',
    severity: 'red',
  },
  {
    value: 'balanced_use',
    fr: 'Utilisation équilibrée',
    en: 'Balanced use',
    severity: 'green',
  },
];

const GRIEFING_OPTIONS: ReadonlyArray<Option<GriefingRisk>> = [
  { value: 'high', fr: 'Risque élevé', en: 'High risk', severity: 'red' },
  { value: 'medium', fr: 'Risque moyen', en: 'Medium risk', severity: 'orange' },
  { value: 'low', fr: 'Risque faible', en: 'Low risk', severity: 'green' },
];

interface SectionDef {
  key: keyof PatchWarPlanInput;
  fr: string;
  en: string;
  options: ReadonlyArray<Option<string>>;
}

const SECTIONS: ReadonlyArray<SectionDef> = [
  { key: 'warStyle', fr: 'Type de guerre', en: 'War style', options: WAR_STYLE_OPTIONS },
  {
    key: 'opFrequency',
    fr: "Fréquence d'opération",
    en: 'Operation frequency',
    options: OP_FREQUENCY_OPTIONS,
  },
  {
    key: 'stockManagement',
    fr: 'Gestion des stocks',
    en: 'Stock management',
    options: STOCK_OPTIONS,
  },
  {
    key: 'griefingRisk',
    fr: 'Risque de grief / alting',
    en: 'Griefing / alting risk',
    options: GRIEFING_OPTIONS,
  },
];

const SEVERITY_CLASSES: Record<Severity, { bg: string; ring: string; dot: string }> = {
  red: {
    bg: 'bg-red-700/70 hover:bg-red-600/80',
    ring: 'ring-red-400',
    dot: 'bg-red-400',
  },
  orange: {
    bg: 'bg-amber-600/70 hover:bg-amber-500/80',
    ring: 'ring-amber-300',
    dot: 'bg-amber-300',
  },
  green: {
    bg: 'bg-emerald-700/70 hover:bg-emerald-600/80',
    ring: 'ring-emerald-300',
    dot: 'bg-emerald-300',
  },
};

const PANEL_WIDTH = 288;

const containerOpenStyle: CSSProperties = { width: PANEL_WIDTH };

export function WarPlanPanel({ canEdit }: WarPlanPanelProps): React.JSX.Element {
  const isOpen = useWarPlanPanel((s) => s.isOpen);
  const toggle = useWarPlanPanel((s) => s.toggle);
  const query = useWarPlan();
  const updateMut = useUpdateWarPlan();

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={toggle}
        title="Show war plan panel"
        aria-label="Show war plan panel"
        className="absolute right-2 top-2 z-30 flex h-10 w-10 items-center justify-center rounded border border-slate-700 bg-slate-900/90 text-slate-100 shadow-lg backdrop-blur transition hover:bg-slate-700"
      >
        <span aria-hidden="true">◀</span>
      </button>
    );
  }

  const dto = query.data ?? null;

  return (
    <aside
      role="complementary"
      aria-label="War plan"
      className="absolute right-2 top-2 bottom-2 z-30 flex flex-col rounded border border-slate-700 bg-slate-900/95 text-slate-100 shadow-lg backdrop-blur"
      style={containerOpenStyle}
    >
      <PanelHeader
        title={dto?.title ?? '…'}
        canEdit={canEdit}
        onCollapse={toggle}
        onCommitTitle={(next) => {
          if (!dto || next === dto.title) return;
          updateMut.mutate({ title: next });
        }}
      />

      <div className="flex-1 overflow-y-auto px-3 py-3">
        {query.isLoading && <p className="text-base text-slate-400">Chargement…</p>}
        {query.isError && (
          <p className="text-base text-red-400">Impossible de charger le war-plan.</p>
        )}
        {dto && (
          <div className="flex flex-col gap-3">
            {SECTIONS.map((section) => {
              const currentValue = dto[section.key as keyof WarPlanDTO] as string;
              return (
                <SectionRow
                  key={section.key}
                  section={section}
                  selected={currentValue}
                  canEdit={canEdit && !updateMut.isPending}
                  onSelect={(value) =>
                    updateMut.mutate({ [section.key]: value })
                  }
                />
              );
            })}
            <UpdatedFooter updatedAt={dto.updatedAt} />
          </div>
        )}
      </div>
    </aside>
  );
}

interface PanelHeaderProps {
  title: string;
  canEdit: boolean;
  onCollapse: () => void;
  onCommitTitle: (next: string) => void;
}

function PanelHeader({
  title,
  canEdit,
  onCollapse,
  onCommitTitle,
}: PanelHeaderProps): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);

  useEffect(() => {
    if (!editing) setDraft(title);
  }, [title, editing]);

  function commit(): void {
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      setDraft(title);
    } else {
      onCommitTitle(trimmed);
    }
    setEditing(false);
  }

  return (
    <header className="flex items-center justify-between gap-2 border-b border-slate-700 px-3 py-2">
      {editing ? (
        <input
          autoFocus
          value={draft}
          maxLength={120}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') {
              setDraft(title);
              setEditing(false);
            }
          }}
          className="flex-1 rounded border border-slate-600 bg-slate-800 px-2 py-1 text-lg font-semibold text-slate-100 outline-none focus:border-emerald-500"
        />
      ) : (
        <button
          type="button"
          onClick={() => canEdit && setEditing(true)}
          title={canEdit ? 'Modifier le titre' : title}
          disabled={!canEdit}
          className={
            'flex-1 truncate text-left text-lg font-semibold ' +
            (canEdit ? 'cursor-text hover:text-emerald-300' : 'cursor-default')
          }
        >
          {title}
        </button>
      )}
      <button
        type="button"
        onClick={onCollapse}
        title="Replier le panneau"
        aria-label="Collapse war plan panel"
        className="flex h-7 w-7 items-center justify-center rounded text-slate-300 transition hover:bg-slate-700 hover:text-slate-100"
      >
        <span aria-hidden="true">▶</span>
      </button>
    </header>
  );
}

interface SectionRowProps {
  section: SectionDef;
  selected: string;
  canEdit: boolean;
  onSelect: (value: string) => void;
}

function SectionRow({
  section,
  selected,
  canEdit,
  onSelect,
}: SectionRowProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent): void {
      const root = rootRef.current;
      if (root && e.target instanceof Node && !root.contains(e.target)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = section.options.find((o) => o.value === selected) ?? section.options[0];
  if (!current) return <></>;

  return (
    <div ref={rootRef} className="flex flex-col gap-1">
      <div className="px-1 text-[14px] uppercase tracking-wide text-slate-300">
        <span className="block leading-tight">{section.fr}</span>
        <span className="block text-[13px] text-slate-400">{section.en}</span>
      </div>

      <button
        type="button"
        onClick={() => canEdit && setOpen((v) => !v)}
        disabled={!canEdit}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={canEdit ? 'Cliquer pour changer' : `${current.fr} / ${current.en}`}
        className={
          'flex items-center gap-2 rounded px-2 py-1.5 text-left text-base text-white shadow transition ' +
          SEVERITY_CLASSES[current.severity].bg +
          (canEdit ? ' cursor-pointer' : ' cursor-default opacity-90') +
          (open ? ' ring-2 ' + SEVERITY_CLASSES[current.severity].ring : '')
        }
      >
        <span
          aria-hidden="true"
          className={'h-2 w-2 shrink-0 rounded-full ' + SEVERITY_CLASSES[current.severity].dot}
        />
        <span className="flex-1 leading-tight">
          <span className="block font-semibold">{current.fr}</span>
          <span className="block text-[13px] text-white/80">{current.en}</span>
        </span>
        {canEdit && (
          <span aria-hidden="true" className="text-white/70">
            {open ? '▴' : '▾'}
          </span>
        )}
      </button>

      {open && canEdit && (
        <ul
          role="listbox"
          aria-label={section.fr}
          className="z-40 flex flex-col gap-1 rounded border border-slate-700 bg-slate-800/95 p-1 shadow-lg"
        >
          {section.options.map((opt) => {
            const isSelected = opt.value === current.value;
            const sev = SEVERITY_CLASSES[opt.severity];
            return (
              <li key={opt.value} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    if (opt.value !== current.value) onSelect(opt.value);
                  }}
                  className={
                    'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-base text-white transition ' +
                    sev.bg +
                    (isSelected ? ' ring-2 ' + sev.ring : '')
                  }
                >
                  <span aria-hidden="true" className={'h-2 w-2 shrink-0 rounded-full ' + sev.dot} />
                  <span className="flex-1 leading-tight">
                    <span className="block font-semibold">{opt.fr}</span>
                    <span className="block text-[13px] text-white/80">{opt.en}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function UpdatedFooter({ updatedAt }: { updatedAt: string }): React.JSX.Element {
  const date = new Date(updatedAt);
  return (
    <p className="pt-1 text-right text-[13px] text-slate-500">
      Mis à jour : {date.toLocaleString()}
    </p>
  );
}
